import styles from './InterviewCard.module.scss';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  InlineNotification,
  NotificationType,
  Select,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';
import { SchedulingOffer, SchedulingSlot } from '../../scheduling.types';
import {
  formatDayFull,
  formatExpiry,
  formatSlotRange,
  listTimeZones,
  timeZoneLabel,
} from '../../timezone';
import SlotPicker from '../SlotPicker/SlotPicker';

interface InterviewCardProps {
  offer: SchedulingOffer;
  timeZone: string;
  onTimeZoneChange: (timeZone: string) => void;
  /** Something the candidate should know before picking again, if anything. */
  notice?: string | null;
  /** A booking is in flight; the button shows it and takes no second press. */
  booking: boolean;
  onBook: (slot: SchedulingSlot) => void;
  onDeclineAll: () => void;
}

/**
 * The pick step: interview summary, time zone, calendar, times and the book
 * button. Pressing it books the chosen time straight away (BL-1608), so
 * everything the candidate commits to — the day and range in their zone, and
 * for a reschedule offer the time it replaces — is shown beside the button.
 */
const InterviewCard: FC<InterviewCardProps> = ({
  offer,
  timeZone,
  onTimeZoneChange,
  notice,
  booking,
  onBook,
  onDeclineAll,
}) => {
  const { t } = useTranslation();

  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const selectedSlot =
    offer.seats.find(s => s.seatId === selectedSlotId) ?? null;

  const timeZoneOptions = useMemo<TSelectOption<string>[]>(() => {
    const zones = listTimeZones();
    const withCurrent = zones.includes(timeZone) ? zones : [timeZone, ...zones];
    const slotStarts = offer.seats.map(seat => seat.startsAt);
    return withCurrent.map(zone => ({
      value: zone,
      label: timeZoneLabel(zone, slotStarts),
    }));
  }, [timeZone, offer.seats]);
  const timeZoneValue = timeZoneOptions.find(o => o.value === timeZone);

  const summary = selectedSlot
    ? t('publicScheduling.youPicked', {
        day: formatDayFull(selectedSlot.startsAt, timeZone),
        range: formatSlotRange(selectedSlot, timeZone),
      })
    : t('publicScheduling.timesOnOffer', {
        count: offer.seats.length,
        expiry: formatExpiry(offer.linkExpiresAt, timeZone),
      });
  const replaces =
    selectedSlot && offer.currentInterview
      ? t('publicScheduling.rescheduleOfferReplaces', {
          day: formatDayFull(offer.currentInterview.startsAt, timeZone),
          range: formatSlotRange(offer.currentInterview, timeZone),
        })
      : null;

  return (
    <section className={styles.card} aria-labelledby="public-scheduling-title">
      <div className={styles.header}>
        <span className={styles.headerIcon} aria-hidden="true">
          <Icon icon="calendar_today" width={24} height={24} />
        </span>
        <div className={styles.headings}>
          <h1 id="public-scheduling-title" className={styles.title}>
            {offer.roleName
              ? t('publicScheduling.roleInterviewTitle', {
                  role: offer.roleName,
                })
              : t('publicScheduling.interviewFallbackTitle')}
          </h1>
          {offer.companyName && (
            <p className={styles.company}>{offer.companyName}</p>
          )}
        </div>
      </div>

      <div className={styles.meta}>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel} id="public-scheduling-tz-label">
            {t('publicScheduling.timeZone')}
          </span>
          <div className={styles.timeZoneSelect}>
            <Select
              id="public-scheduling-tz"
              options={timeZoneOptions}
              value={timeZoneValue}
              ariaLabel={t('publicScheduling.yourTimeZone')}
              menuPosition="fixed"
              hasClearIndicator={false}
              onChange={option =>
                option.value && onTimeZoneChange(option.value)
              }
            />
          </div>
          <span className={styles.metaLabelInline}>
            {t('publicScheduling.duration')}
          </span>
          <span className={styles.metaValue}>
            {t('publicScheduling.durationMinutes', {
              count: offer.durationMinutes,
            })}
          </span>
        </div>
      </div>

      {offer.currentInterview && (
        <div className={styles.notice}>
          <InlineNotification notificationType={NotificationType.INFORMATION}>
            <p>
              {t('publicScheduling.rescheduleOfferNotice', {
                day: formatDayFull(offer.currentInterview.startsAt, timeZone),
                range: formatSlotRange(offer.currentInterview, timeZone),
              })}
            </p>
          </InlineNotification>
        </div>
      )}

      {notice && (
        <div className={styles.notice}>
          <InlineNotification notificationType={NotificationType.INFORMATION}>
            <p>{notice}</p>
          </InlineNotification>
        </div>
      )}

      <div className={styles.picker}>
        <SlotPicker
          slots={offer.seats}
          timeZone={timeZone}
          selectedSeatId={selectedSlotId}
          onSelect={setSelectedSlotId}
        />
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <span className={styles.summaryText}>{summary}</span>
          {replaces && <span className={styles.summaryText}>{replaces}</span>}
          <button
            type="button"
            className={styles.declineLink}
            onClick={onDeclineAll}
          >
            {t('publicScheduling.cantDoAnyDay')}
          </button>
        </div>
        <Button
          variant="primary"
          size="medium"
          className={styles.confirm}
          disabled={!selectedSlot || booking}
          loading={booking}
          onClick={() => selectedSlot && onBook(selectedSlot)}
        >
          {selectedSlot
            ? t('publicScheduling.bookThisTime')
            : t('publicScheduling.pickATime')}
        </Button>
      </div>
    </section>
  );
};

export default InterviewCard;
